import React, { useState, useEffect } from "react";
import {Card} from "../../common";
//#region 型定義
interface FilePreviewProps {
  /** プレビュー対象のファイル */
  file: File;
  /** プレビューのタイトル */
  title: string;
  /** プレビューのタイプ（入力/出力） */
  type: "input" | "output";
}

/** ファイルの内容とメタデータを保持する型 */
interface FileContent {
  /** ファイルの内容（テキスト） */
  text: string;
  /** ファイルのMIMEタイプ */
  mimeType: string;
  /** ファイルのサイズ（バイト） */
  size: number;
}
//#endregion

/**
 * ファイルプレビューコンポーネント
 * 入力・出力ファイルの内容をテキストとして表示する
 *
 * @param props コンポーネントのプロパティ
 * @returns プレビュー表示用のJSX
 */
export const FilePreview: React.FC<FilePreviewProps> = ({
  file,
  title,
  type,
}) => {
  //#region ステート定義
  /** ファイル内容の状態 */
  const [fileContent, setFileContent] = useState<FileContent | null>(null);
  /** エラー���ッセージの状態 */
  const [error, setError] = useState<string | null>(null);
  /** ローディング状態 */
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion

  //#region ファイル内容の読み込み処理
  useEffect(() => {
    /**
     * ファイルの内容を読み込む非同期関数
     */
    const readFileContent = async () => {
      if (!file) return;

      setIsLoading(true);
      setError(null);

      try {
        const reader = new FileReader();

        // ファイル読み込み完了時の処理
        reader.onload = (event) => {
          const content = event.target?.result;
          if (content) {
            // 出力タイプがレポートの場合はShift-JISでデコード
            if (file.name.toLowerCase().endsWith(".log")) {
              const decoder = new TextDecoder("shift-jis");
              const decodedContent = decoder.decode(
                new Uint8Array(content as ArrayBuffer)
              );
              setFileContent({
                text: decodedContent,
                mimeType: file.type || "text/plain",
                size: file.size,
              });
            } else {
              // その他のファイルは通常のテキストとして読み込み
              setFileContent({
                text: content as string,
                mimeType: file.type || "text/plain",
                size: file.size,
              });
            }
          }
          setIsLoading(false);
        };

        // ファイル読み込みエラー時の処理
        reader.onerror = () => {
          setError("ファイルの読み込みに失敗しました");
          setIsLoading(false);
        };

        // .logファイルの場合はArrayBufferとして読み込み、それ以外はテキストとして読み込み
        if (file.name.toLowerCase().endsWith(".log")) {
          reader.readAsArrayBuffer(file);
        } else {
          reader.readAsText(file);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "予期せぬエラーが発生しました";
        setError(errorMessage);
        setIsLoading(false);
        console.error("ファイル読み込みエラー:", err);
      }
    };

    readFileContent();
  }, [file]);
  //#endregion

  //#region ファイルサイズのフォーマット処理
  /**
   * ファイルサイズを適切な単位でフォーマットする
   * @param bytes ファイルサイズ（バイト）
   * @returns フォーマットされたサイズ文字列（例：1.5 KB）
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  //#endregion

  //#region プレビューコンテンツの生成処理
  /**
   * プレビューのコンテンツを生成する
   * @returns プレビューコンテンツのJSX
   */
  const renderPreviewContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-4 text-red-600 bg-red-50 rounded-md">
          <p className="font-medium">エラーが発生しました</p>
          <p className="mt-1 text-sm">{error}</p>
        </div>
      );
    }

    if (fileContent) {
      return (
        <div className="overflow-auto h-96 border border-gray-200 rounded-md">
          <pre className="p-4 bg-gray-50 text-sm font-mono whitespace-pre-wrap break-words">
            {fileContent.text}
          </pre>
        </div>
      );
    }

    return (
      <div className="flex justify-center items-center h-64 bg-gray-50 rounded-md">
        <p className="text-gray-500">プレビューはありません</p>
      </div>
    );
  };
  //#endregion

  //#region コンポーネントのレンダリング処理
  return (
    <Card
      title={title}
      className={`mt-4 ${type === "input" ? "border-blue-200" : "border-green-200"}`}
      shadow="sm"
    >
      <div className="p-4">
        {/* ファイル情報の表示 */}
        <div className="mb-4 space-y-2">
          <div className="text-sm text-gray-600">
            <span className="font-medium">ファイル名:</span> {file.name}
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium">ファイルサイズ:</span>{" "}
            {formatFileSize(file.size)}
          </div>
          {fileContent?.mimeType && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">ファイルタイプ:</span>{" "}
              {fileContent.mimeType}
            </div>
          )}
        </div>

        {/* プレビューコンテンツの表示 */}
        {renderPreviewContent()}
      </div>
    </Card>
  );
  //#endregion
};
